import React from 'react';

import { CircularProgress, Typography } from '@mui/material';
import { AlineButton } from 'src/components';
import { OCRStatusOrChildren } from 'src/editor/toolbars/right-panel/ai/shared/OCRStatusOrChildren';
import { useDocumentParams } from 'src/helpers';
import { useConvertToAline, useGetCurrentDoc } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  loadingDocument: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    height: '100%',
    justifyContent: 'center',
    padding: theme.spacing(0, 4),
  },
  root: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    height: '100%',
  },
}));

export function AlineDocumentGuard({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}) {
  const { classes } = useStyles();
  const { document } = useGetCurrentDoc();
  const { documentId } = useDocumentParams();
  const {
    convertToAline,
    getIsActivelyConverting,
    getIsDocumentConvertable,
    loading: isConvertingToAline,
  } = useConvertToAline();
  const isDocumentConverting = getIsActivelyConverting(document);
  const isDocumentConvertable = getIsDocumentConvertable(document);

  if (isConvertingToAline || isDocumentConverting) {
    return (
      <div className={classes.root}>
        <div className={classes.loadingDocument}>
          <CircularProgress size={28} />
          <Typography align="center">Converting to Aline...</Typography>
        </div>
      </div>
    );
  }

  if (isDocumentConvertable) {
    return (
      <div className={classes.root}>
        <div className={classes.loadingDocument}>
          <Typography align="center" variant="subtitle2">
            {`Click "Edit" to use ${title} with this MS Word document.`}
          </Typography>
          <AlineButton
            color="primary"
            variant="contained"
            onClick={async () => {
              await convertToAline(documentId);
            }}
          >
            Edit
          </AlineButton>
        </div>
      </div>
    );
  }

  return <OCRStatusOrChildren documentId={documentId}>{children}</OCRStatusOrChildren>;
}
